/*
 * Copyright 2019 VMware, Inc.
 * SPDX-License-Identifier: EPL-2.0
 */
import { InjectionToken } from "@angular/core";
import { I18nContext } from "./services/i18n.context";
import { isDefined } from "./util";

export const VIP_CONFIG: InjectionToken<VIPConfigRef> = new InjectionToken<VIPConfigRef>("VIP_CONFIG");
export type VIPConfigRef = Required<VIPConfig>;
export enum PatternCategories {
    DATE = "dates",
    NUMBER = "numbers",
    PLURAL = "plurals",
    CURRENCIES = "currencies",
    DATEFIELDS = "dateFields",
}

export interface VIPConfig {
    productID: string;
    component: string;
    version: string;
    host?: string;
    isolated?: boolean;
    isPseudo?: boolean;
    i18nScope?: PatternCategories[];
    sourceBundles?: { [key: string]: any }[];
    translationBundles?: { [key: string]: any };
    i18nAssets?: string;
    timeout?: number;
    // deprecated
    language?: string;
    region?: string;
    locale?: string;
    sourceBundle?: { [key: string]: any };
    collectSource?: boolean;
}

export interface VIPChildConfig {
    productID?: string;
    component: string;
    version?: string;
    host?: string;
    isolated?: boolean;
    isPseudo?: boolean;
    i18nScope?: PatternCategories[];
    sourceBundles?: { [key: string]: any }[];
    translationBundles?: { [key: string]: any };
    i18nAssets?: string;
    timeout?: number;
}

export function getNameSpace(config: VIPConfig) {
    if (!config) {
        return undefined;
    }
    return [config.productID, config.component, config.version]
        .join("-")
        .toString()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w\-]+/g, "")
        .replace(/\-\-+/g, "-")
        .replace(/^-+/, "")
        .replace(/-+$/, "");
}

/**
 * Check and set default value. i18n context will override some environmental parameters.
 * @param vipConfig
 */
export function VIPConfigFactory(vipConfig: VIPConfig, i18nContext: I18nContext): VIPConfigRef {
    // The isPseudo default value is false,
    // isPseudo config in i18nContext has the highest priority,
    // and will override other configurations.
    const isPseudo = isDefined(i18nContext.pseudoEnabled) ? i18nContext.pseudoEnabled : vipConfig.isPseudo || false;
    return {
        productID: vipConfig.productID || undefined,
        component: vipConfig.component || undefined,
        version: vipConfig.version || undefined,
        host: vipConfig.host || undefined,
        isolated: isDefined(vipConfig.isolated) ? vipConfig.isolated : true,
        isPseudo,
        i18nScope: vipConfig.i18nScope || [],
        sourceBundles: vipConfig.sourceBundles || undefined,
        translationBundles: vipConfig.translationBundles || undefined,
        i18nAssets: vipConfig.i18nAssets || undefined,
        timeout: vipConfig.timeout || 3000,
        locale: vipConfig.locale || undefined,
        region: vipConfig.region || undefined,
        language: vipConfig.language || "en",
        sourceBundle: vipConfig.sourceBundle || undefined,
        collectSource: vipConfig.collectSource || false,
    };
}
